/**
 * Admin → Review (PRP-MCP-9 work item 7, G6, founder decision F7): the
 * sanitized copies of estimates the assistant created or changed.
 *
 * Newest first, one row each: date, user, chat app, loss type, rooms, lines,
 * from-chat lines, and the import question's answer. Filters: import answer,
 * date range, and a user (the admin user page links `/admin/review?user=<id>`).
 * A row opens the snapshot as a read-only estimate: rooms with dimensions, lines
 * (code, description, action, quantity, calc, unit, heading, from chat), the
 * chat change log (each change as kind, code and action; no free-text request
 * summary is kept, DD-9.C8), and the imported Original. The copies hold no
 * insured name, address, claim or policy number, contact details or notes;
 * the server never sends them.
 *
 * Filtered to one user, a Delete button removes all of that user's snapshots
 * after a confirm (the consent promise: "You can ask us to delete your copies").
 * Admin only on the server; a non-admin gets 403 and sees the error.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { AdminReviewAPI } from '../../lib/adminReviewApi';
import type {
  ImportAnswerFilter,
  SnapshotDetail,
  SnapshotRoom,
  SnapshotRow,
} from '../../lib/adminReviewApi';

const PAGE_SIZE = 50;

const ANSWER_LABEL: Record<string, string> = {
  yes: 'Yes',
  no: 'No',
  not_yet: 'Not yet',
};

const ANSWER_FILTERS: { value: '' | ImportAnswerFilter; label: string }[] = [
  { value: '', label: 'Any import answer' },
  { value: 'yes', label: 'Imported: yes' },
  { value: 'no', label: 'Imported: no' },
  { value: 'not_yet', label: 'Imported: not yet' },
  { value: 'none', label: 'No answer yet' },
];

const fmtTime = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');
const dash = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : String(v));

function errText(err: unknown, fallback: string): string {
  const e = err as { message?: string; status?: number };
  if (e?.status === 403) return 'Admins only.';
  return e?.message || fallback;
}

/** One room of a snapshot as a read-only line table. */
const RoomTable: React.FC<{ room: SnapshotRoom }> = ({ room }) => {
  const dims = Object.entries(room.dimensions || {})
    .map(([k, v]) => `${k.replace(/_/g, ' ')} ${v}`)
    .join(' · ');
  return (
    <div className="space-y-1">
      <div className="text-sm font-medium">
        {room.name}
        {dims && <span className="ml-2 text-xs font-normal text-muted-foreground">{dims}</span>}
      </div>
      {room.lines.length === 0 ? (
        <p className="text-xs text-muted-foreground">No lines.</p>
      ) : (
        <table className="w-full text-xs">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="p-1">Heading</th>
              <th className="p-1">Code</th>
              <th className="p-1">Description</th>
              <th className="p-1">Action</th>
              <th className="p-1">Qty</th>
              <th className="p-1">Calc</th>
              <th className="p-1">Unit</th>
              <th className="p-1">From chat</th>
            </tr>
          </thead>
          <tbody>
            {room.lines.map((ln, i) => (
              <tr key={`${ln.line}-${i}`} className="border-t border-border">
                <td className="p-1">{dash(ln.heading)}</td>
                <td className="p-1 font-mono">{ln.code}</td>
                <td className="p-1">{ln.description}</td>
                <td className="p-1">{dash(ln.action)}</td>
                <td className="p-1">{dash(ln.quantity)}</td>
                <td className="p-1 font-mono">{dash(ln.calc)}</td>
                <td className="p-1">{dash(ln.unit)}</td>
                <td className="p-1">{ln.from_chat ? 'Yes' : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

/** The read-only estimate view of one snapshot. */
const SnapshotView: React.FC<{ detail: SnapshotDetail; onClose: () => void }> = ({ detail, onClose }) => {
  const s = detail.snapshot;
  const [showOriginal, setShowOriginal] = useState(false);
  return (
    <div className="space-y-4 rounded-md border border-border p-4" data-testid="snapshot-view">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-semibold">Snapshot {fmtTime(detail.created_at)}</h2>
        <span className="text-sm text-muted-foreground">
          {dash(detail.user_email || detail.user_id)} · {dash(s.chat_app)} ·{' '}
          {detail.reason === 'trial_end' ? 'trial end' : 'export'}
        </span>
        <button className="ml-auto rounded-md border border-border px-3 py-1 text-sm" onClick={onClose}>
          Close
        </button>
      </div>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm md:grid-cols-4">
        <dt className="text-muted-foreground">Loss type</dt>
        <dd>{dash(s.loss_type)}{s.is_flood_claim ? ' (flood claim)' : ''}</dd>
        <dt className="text-muted-foreground">State</dt>
        <dd>{dash(s.state)}</dd>
        <dt className="text-muted-foreground">Water category</dt>
        <dd>{dash(s.water_category)}</dd>
        <dt className="text-muted-foreground">Created in chat</dt>
        <dd>{s.created_in_chat ? 'Yes' : 'No, imported'}</dd>
        <dt className="text-muted-foreground">Import answer</dt>
        <dd>{detail.import_answer ? ANSWER_LABEL[detail.import_answer] : 'No answer'}</dd>
      </dl>
      <div className="space-y-3">
        {s.rooms.map((r, i) => (
          <RoomTable key={`${r.name}-${i}`} room={r} />
        ))}
      </div>
      <div className="space-y-1">
        <h3 className="text-sm font-semibold">Chat changes ({s.changes.length})</h3>
        {s.changes.length === 0 ? (
          <p className="text-xs text-muted-foreground">None.</p>
        ) : (
          <ul className="space-y-1 text-xs">
            {s.changes.map((c) => (
              <li key={c.change_id} className="border-t border-border pt-1">
                <span className="font-mono">{c.operation || c.kind}</span>
                {c.room && <span> · {c.room}</span>}
                {c.undone && <span className="text-muted-foreground"> · undone</span>}
                {c.before && <span className="text-muted-foreground"> · before {JSON.stringify(c.before)}</span>}
                {c.after && <span className="text-muted-foreground"> · after {JSON.stringify(c.after)}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
      {s.original && (
        <div className="space-y-2">
          <button
            className="rounded-md border border-border px-3 py-1 text-sm"
            onClick={() => setShowOriginal((v) => !v)}
          >
            {showOriginal ? 'Hide the imported Original' : 'Show the imported Original'}
          </button>
          {showOriginal && s.original.rooms.map((r, i) => <RoomTable key={`o-${r.name}-${i}`} room={r} />)}
        </div>
      )}
    </div>
  );
};

/** The /admin/review page. */
const AdminReviewPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const user = params.get('user') ?? '';
  const answer = (params.get('answer') ?? '') as '' | ImportAnswerFilter;
  const dateFrom = params.get('from') ?? '';
  const dateTo = params.get('to') ?? '';
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState<SnapshotRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [detail, setDetail] = useState<SnapshotDetail | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await AdminReviewAPI.list({
        ...(answer ? { answer } : {}),
        ...(dateFrom ? { date_from: dateFrom } : {}),
        ...(dateTo ? { date_to: dateTo } : {}),
        ...(user ? { user } : {}),
        limit: PAGE_SIZE,
        offset,
      });
      setRows(res.items ?? []);
      setTotal(res.total ?? 0);
    } catch (err) {
      setError(errText(err, 'Could not load the snapshots.'));
    } finally {
      setLoading(false);
    }
  }, [answer, dateFrom, dateTo, user, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
    setOffset(0);
    setDetail(null);
  };

  const open = async (id: string) => {
    setError(null);
    try {
      setDetail(await AdminReviewAPI.get(id));
    } catch (err) {
      setError(errText(err, 'Could not open that snapshot.'));
    }
  };

  const deleteAll = async () => {
    if (!user) return;
    const who = rows[0]?.user_email || user;
    if (!window.confirm(`Delete every snapshot of ${who}? This cannot be undone.`)) return;
    try {
      const res = await AdminReviewAPI.deleteForUser(user);
      setNote(`Deleted ${res.deleted} snapshot${res.deleted === 1 ? '' : 's'} of ${who}.`);
      setDetail(null);
      await load();
    } catch (err) {
      setError(errText(err, 'Could not delete the snapshots.'));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold">Assistant review</h1>
        <Link to="/admin" className="text-sm text-muted-foreground underline">
          Admin
        </Link>
      </div>
      <p className="text-sm text-muted-foreground">
        Sanitized copies of estimates the assistant created or changed, written at export and at trial end.
        No insured name, address, claim or policy number, contact details or notes are kept.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label="Import answer"
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          value={answer}
          onChange={(e) => setFilter('answer', e.target.value)}
        >
          {ANSWER_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
        <label className="text-sm text-muted-foreground">
          From{' '}
          <input
            type="date"
            aria-label="From date"
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            value={dateFrom}
            onChange={(e) => setFilter('from', e.target.value)}
          />
        </label>
        <label className="text-sm text-muted-foreground">
          To{' '}
          <input
            type="date"
            aria-label="To date"
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            value={dateTo}
            onChange={(e) => setFilter('to', e.target.value)}
          />
        </label>
        {user && (
          <span className="flex items-center gap-2 rounded-md border border-border px-2 py-1 text-sm">
            User: {rows[0]?.user_email || user}
            <button aria-label="Clear user filter" className="text-muted-foreground" onClick={() => setFilter('user', '')}>
              ×
            </button>
          </span>
        )}
        {user && (
          <button
            className="rounded-md border border-red-500/50 px-3 py-1.5 text-sm text-red-600 dark:text-red-400"
            onClick={() => void deleteAll()}
          >
            Delete all snapshots of this user
          </button>
        )}
        <span className="text-sm text-muted-foreground">{loading ? 'Loading…' : `${total} snapshots`}</span>
      </div>
      {error && <p className="text-sm font-medium text-red-500">{error}</p>}
      {note && <p className="text-sm text-muted-foreground">{note}</p>}
      {detail && <SnapshotView detail={detail} onClose={() => setDetail(null)} />}
      {!loading && rows.length === 0 && !error ? (
        <p className="text-sm text-muted-foreground">No snapshots match.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="p-2">Date</th>
                <th className="p-2">User</th>
                <th className="p-2">Chat app</th>
                <th className="p-2">Loss type</th>
                <th className="p-2">Rooms</th>
                <th className="p-2">Lines</th>
                <th className="p-2">From chat</th>
                <th className="p-2">Import answer</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="cursor-pointer border-t border-border hover:bg-muted/30"
                  onClick={() => void open(r.id)}
                >
                  <td className="p-2 whitespace-nowrap">{fmtTime(r.created_at)}</td>
                  <td className="p-2">{r.user_email || r.user_id}</td>
                  <td className="p-2">{dash(r.chat_app)}</td>
                  <td className="p-2">{dash(r.loss_type)}</td>
                  <td className="p-2">{r.room_count}</td>
                  <td className="p-2">{r.line_count}</td>
                  <td className="p-2">{r.chat_line_count}</td>
                  <td className="p-2">{r.import_answer ? ANSWER_LABEL[r.import_answer] : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {total > PAGE_SIZE && (
        <div className="flex gap-2">
          <button
            className="rounded-md border border-border px-3 py-1 text-sm disabled:opacity-50"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Previous
          </button>
          <button
            className="rounded-md border border-border px-3 py-1 text-sm disabled:opacity-50"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminReviewPage;
